/**
 * AnalyticsService — tracking GA4 cross-platform (web + mobile)
 *
 * Web : utilise window.gtag injecté via dist/index.html (script gtag.js).
 *       Tous les events passent par gtag('event', ...) → console GA4 standard.
 *
 * Mobile : utilise GA4 Measurement Protocol via fetch.
 *          Endpoint : https://www.google-analytics.com/mp/collect
 *          Requiert MEASUREMENT_ID + API_SECRET (à générer dans
 *          GA4 Admin → Data streams → Mytenue → Measurement Protocol API secrets).
 *
 * Si API_SECRET absent → mobile : no-op silencieux (events ignorés). Web continue
 * de tracker normalement via gtag.
 *
 * Usage :
 *   import { trackEvent, trackScreen } from '../services/AnalyticsService';
 *   trackEvent('outfit_created', { items_count: 4 });
 *   trackScreen('Home');
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
// Pont Firebase Analytics (natif uniquement — stub no-op sur web via .web.js).
// Sert à alimenter les conversions in-app des campagnes Google Ads.
import { logFirebaseEvent, setFirebaseCollection, setFirebaseUserId, isFirebaseReady } from './FirebaseAnalytics';

const MEASUREMENT_ID = 'G-LMX56CHL25';
// API Secret GA4 (Admin → Data streams → Mytenue → Measurement Protocol API secrets)
const API_SECRET = 'vzGeMrAFRVCwa0tlYOzV0g';

const MP_ENDPOINT = `https://www.google-analytics.com/mp/collect?measurement_id=${MEASUREMENT_ID}&api_secret=${API_SECRET}`;
const CLIENT_ID_KEY = 'outfit_ga_client_id';

let _cachedClientId = null;

// v1.12.941 — Pays de l'appareil (expo-localization) pour fiabiliser la géoloc GA.
// Le rapport « Localisation » de GA est basé sur l'IP et lacunaire (VPN, IPv6,
// restriction UE…). La région appareil est instantanée, sans permission, et
// disponible à CHAQUE session → on l'attache à tous les events comme dimension.
let _Localization = null;
try { _Localization = require('expo-localization'); } catch (_) {}
let _cachedCountry = undefined; // undefined = non lu ; null = inconnu
function getDeviceCountry() {
  if (_cachedCountry !== undefined) return _cachedCountry;
  let country = null;
  try {
    if (_Localization && typeof _Localization.getLocales === 'function') {
      const loc = _Localization.getLocales()[0] || {};
      country = loc.regionCode || null; // ex: 'FR', 'US'
    } else if (typeof navigator !== 'undefined' && navigator.language) {
      const part = navigator.language.split('-')[1];
      country = part ? part.toUpperCase() : null;
    }
  } catch (_) {}
  _cachedCountry = country;
  return country;
}
// v1.13.50 — Version de l'app (app.json → change à chaque OTA). Attachée à TOUS
// les events (dont login) + en user_property GA → on voit dans GA quelle version
// chaque utilisateur exécute.
let _Constants = null;
try { _Constants = require('expo-constants').default || require('expo-constants'); } catch (_) {}
let _cachedAppVersion = undefined;
function getAppVersion() {
  if (_cachedAppVersion !== undefined) return _cachedAppVersion;
  let v = null;
  try {
    v = (_Constants && (
      _Constants.expoConfig?.version
      || _Constants.manifest?.version
      || _Constants.manifest2?.extra?.expoClient?.version
    )) || null;
  } catch (_) {}
  _cachedAppVersion = v;
  return v;
}
// v1.12.681 — Init gtag.js sur web. Le dist/index.html généré par expo export
// n'inclut PAS le script gtag → window.gtag absent → les events tombaient sur
// le Measurement Protocol fetch qui échoue en CORS (MP = server-side only).
// On injecte donc gtag.js dynamiquement au boot.
let _gtagInitStarted = false;
export function initWebAnalytics() {
  if (Platform.OS !== 'web' || typeof document === 'undefined' || typeof window === 'undefined') return;
  if (_gtagInitStarted || typeof window.gtag === 'function') return;
  _gtagInitStarted = true;
  try {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', MEASUREMENT_ID, { send_page_view: false });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(s);
    console.log('[Analytics] gtag.js injected for web');
  } catch (e) {
    console.warn('[Analytics] gtag init failed:', e?.message);
  }
}
// v1.12.172 — session_id : généré au 1er event de la session (lifetime app)
// Requis par GA4 pour compter les events comme une session valide.
let _sessionId = null;
let _sessionStart = null;
function getSessionId() {
  if (!_sessionId) {
    _sessionId = String(Math.floor(Date.now() / 1000));
    _sessionStart = Date.now();
  }
  return _sessionId;
}
function getEngagementMs() {
  if (!_sessionStart) _sessionStart = Date.now();
  return Math.max(1, Date.now() - _sessionStart);
}
// v1.13.52 — Formats LISIBLES du temps d'engagement pour GA (le param standard
// engagement_time_msec reste en ms, requis par GA4). On expose en plus :
//   • engagement_seconds : entier en secondes (à enregistrer comme métrique perso)
//   • engagement_mmss    : "m:ss" (à enregistrer comme dimension perso)
function msToMMSS(ms) {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

async function getClientId() {
  if (_cachedClientId) return _cachedClientId;
  try {
    let id = await AsyncStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = `${Date.now()}.${Math.random().toString(36).slice(2, 12)}`;
      await AsyncStorage.setItem(CLIENT_ID_KEY, id);
    }
    _cachedClientId = id;
    return id;
  } catch {
    return `${Date.now()}.fallback`;
  }
}

// ─── v1.12.966 — Consentement RGPD (cookies/analytics) ─────────────────────
// Aucune mesure d'audience ni géo n'est envoyée sans consentement.
//   • Web   : strict — on ne track QUE si le bandeau a été 'accepted'
//             (refus OU pas encore de choix → rien, conforme RGPD).
//   • Mobile : analyses anonymisées (intérêt légitime, cf politique de
//             confidentialité) → on track SAUF refus explicite 'declined'.
// Le choix peut être posé via setAnalyticsConsent() (bandeau web/mobile).
const CONSENT_KEY = 'outfit_cookie_consent';
let _consentMobile; // undefined = pas lu ; 'accepted' | 'declined' | null
export function setAnalyticsConsent(choice) {
  _consentMobile = (choice === 'accepted' || choice === 'declined') ? choice : null;
  try { if (Platform.OS !== 'web') AsyncStorage.setItem(CONSENT_KEY, _consentMobile || ''); } catch (_) {}
  // RGPD : la collecte Firebase ne démarre QUE sur consentement explicite.
  try { setFirebaseCollection(_consentMobile === 'accepted'); } catch (_) {}
}
// Propage tout changement de consentement (bandeau web/mobile → 'cookie_consent_changed').
try { require('react-native').DeviceEventEmitter.addListener('cookie_consent_changed', (p) => setAnalyticsConsent(p?.choice)); } catch (_) {}
// Init mobile : lit le choix éventuellement stocké (au cas où un bandeau natif l'ait posé)
try {
  if (Platform.OS !== 'web') {
    AsyncStorage.getItem(CONSENT_KEY).then(v => {
      if (_consentMobile === undefined) _consentMobile = v || null;
      // Aligne la collecte Firebase sur le consentement stocké au démarrage.
      try { setFirebaseCollection(v === 'accepted'); } catch (_) {}
    }).catch(() => {});
  }
} catch (_) {}
function consentState() {
  if (Platform.OS === 'web') {
    try { return (typeof window !== 'undefined' && window.localStorage) ? (window.localStorage.getItem(CONSENT_KEY) || null) : null; } catch { return null; }
  }
  return _consentMobile === undefined ? null : _consentMobile;
}
/** True si l'on a le droit d'envoyer de la mesure d'audience (geo incluse). */
function analyticsAllowed() {
  const c = consentState();
  if (Platform.OS === 'web') return c === 'accepted';      // strict opt-in
  // v1.12.992 — mobile aussi en OPT-IN strict (RGPD/ePrivacy : GA4 non exempté).
  // Aucune collecte tant que l'user n'a pas accepté via le popup de 1er lancement.
  return c === 'accepted';
}

// v1.12.966 — Géo fiable depuis le profil (backend résout IP→pays/ville au login,
// cf updateLoginGeoAsync). GA4 Measurement Protocol (mobile) ne renseigne PAS les
// dimensions géo standard → on les envoie en user_properties (pays/région/ville)
// pour les rendre disponibles comme dimensions personnalisées GA4.
// ⚠️ N'est attachée QUE si analyticsAllowed() (consentement respecté).
let _userGeo = { country: null, region: null, city: null };
export function setUserGeo(geo = {}) {
  if (geo.country) _userGeo.country = String(geo.country).slice(0, 32);
  if (geo.region)  _userGeo.region  = String(geo.region).slice(0, 64);
  if (geo.city)    _userGeo.city    = String(geo.city).slice(0, 64);
}
function getGeoParams() {
  const country = _userGeo.country || getDeviceCountry();
  return {
    ...(country ? { country } : {}),
    ...(_userGeo.region ? { region: _userGeo.region } : {}),
    ...(_userGeo.city ? { city: _userGeo.city } : {}),
  };
}

// v1.13.29 — User-Agent mobile réaliste pour que GA4 (Measurement Protocol →
// stream web) classe l'app en device category "mobile" (et non "desktop").
function mobileUserAgent() {
  if (Platform.OS === 'android') {
    return 'Mozilla/5.0 (Linux; Android 14; Pixel 8) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Mobile Safari/537.36';
  }
  // iOS (et défaut natif)
  return 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.4 Mobile/15E148 Safari/604.1';
}

/** Track un event arbitraire (cross-platform). */
export async function trackEvent(eventName, params = {}) {
  if (!eventName) return;
  // RGPD : aucune mesure d'audience ni géo sans consentement (web strict opt-in,
  // mobile = tout sauf refus explicite).
  if (!analyticsAllowed()) return;
  try {
    const _geo = getGeoParams();
    const _ver = getAppVersion();
    const _engMs = getEngagementMs();
    const enrichedParams = {
      ...params,
      platform: Platform.OS,
      session_id: getSessionId(),
      engagement_time_msec: _engMs, // requis par GA4 (ms) — ne pas retirer
      engagement_seconds: Math.round(_engMs / 1000), // métrique perso (secondes)
      engagement_mmss: msToMMSS(_engMs), // dimension perso ("m:ss")
      ...(_ver ? { app_version: _ver } : {}),
      ..._geo,
    };
    if (Platform.OS === 'web') {
      // v1.12.681 — Web : TOUJOURS via gtag (jamais le MP fetch qui CORS-fail).
      // Si gtag pas encore prêt, on tente l'init puis on push quand même —
      // gtag bufferise dans dataLayer même avant le chargement du script.
      if (typeof window !== 'undefined') {
        if (typeof window.gtag !== 'function') initWebAnalytics();
        if (typeof window.gtag === 'function') {
          // Géo + version en user_properties (dimensions perso) — le rapport
          // Localisation standard reste alimenté par l'IP côté gtag.
          const _up = { ..._geo, ...(_ver ? { app_version: _ver } : {}) };
          if (Object.keys(_up).length) window.gtag('set', 'user_properties', _up);
          window.gtag('event', eventName, enrichedParams);
        }
      }
      return;
    }
    // ─── Natif (iOS/Android) ───────────────────────────────────────────────
    // 1) Firebase Analytics → conversions in-app pour Google Ads (no-op si
    //    module absent ou collecte non consentie). Gardé hors du gate API_SECRET.
    logFirebaseEvent(eventName, enrichedParams);
    // 2) GA4 Measurement Protocol (dashboards GA4). Toujours actif (garantit les
    // données mobiles dans le property G-LMX56CHL25, quel que soit le build).
    // Le UA mobile ci-dessous corrige la classification "desktop".
    // NB : si le projet Firebase est lié au MÊME property GA4, dé-dupliquer plus
    // tard (gate sur isFirebaseReady) une fois le lien confirmé.
    if (!API_SECRET) return;
    const cid = await getClientId();
    const _userProps = Object.fromEntries(Object.entries(_geo).map(([k, v]) => [k, { value: v }]));
    if (_ver) _userProps.app_version = { value: _ver };
    const payload = {
      client_id: cid,
      ...(Object.keys(_userProps).length ? { user_properties: _userProps } : {}),
      events: [{ name: eventName, params: enrichedParams }],
    };
    // iOS : consommer le body de la réponse force l'envoi réel de la requête.
    fetch(MP_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // v1.13.29 — FIX "app classée desktop" : GA4 (stream web) déduit la
        // device category du User-Agent. `MyTenue/ios` n'est pas reconnu → desktop.
        // On envoie un UA mobile réaliste → GA4 classe en "mobile" (iOS/Android).
        'User-Agent': mobileUserAgent(),
      },
      body: JSON.stringify(payload),
    })
      .then(res => res.text().catch(() => ''))
      .catch(() => {});
  } catch (e) {}
}

/**
 * Track un appui sur un bouton d'action (cross-platform).
 * Convention de nommage : `ui_<zone>_<action>` (ex: 'ui_createlook_next',
 * 'ui_calendar_save_outfit', 'ui_prefs_open'). Émis comme event GA4 `ui_tap`
 * avec un paramètre `button` = le nom → un seul event filtrable par `button`
 * dans GA, plus simple à explorer que N events distincts.
 */
export function trackTap(button, params = {}) {
  if (!button) return;
  trackEvent('ui_tap', { button, ...params });
}

/** Track un changement d'écran (page_view). */
export function trackScreen(screenName) {
  if (!screenName) return;
  trackEvent('screen_view', {
    screen_name: screenName,
    page_title: screenName,
  });
}

/** Track app open (à appeler au mount de App). */
export function trackAppOpen() {
  trackEvent('app_open', { platform: Platform.OS });
}

/** Set user_id pour relier les sessions (optionnel, après login). */
export async function setUserId(userId) {
  if (!userId) return;
  if (!analyticsAllowed()) return; // RGPD : pas d'identifiant sans consentement
  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        if (typeof window.gtag !== 'function') initWebAnalytics();
        if (typeof window.gtag === 'function') {
          window.gtag('set', { user_id: String(userId) });
        }
      }
      return;
    }
    // Natif : relie aussi le user_id côté Firebase (conversions Google Ads).
    setFirebaseUserId(userId);
    if (!API_SECRET) return;
    const cid = await getClientId();
    fetch(MP_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: cid,
        user_id: String(userId),
        events: [{ name: 'user_engagement', params: { engagement_time_msec: 100 } }],
      }),
    }).catch(() => {});
  } catch (e) {}
}
